import React, { useState } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Form,
  InputGroup,
  Alert,
  Spinner,
  ProgressBar,
  Tooltip,
  OverlayTrigger,
  Container,
} from 'react-bootstrap';
import {
  QrCode,
  Search,
  PersonCircle,
  CheckCircle,
  XCircle,
  InfoCircle,
  Envelope,
  Telephone,
  Calendar,
  Droplet,
  Shield,
  Activity,
  FileText,
  GraphUp,
  CalendarEvent,
  Prescription,
  Heart,
  Award,
} from 'react-bootstrap-icons';

// Données fictives des patients (enrichies)
const mockPatients = [
  {
    id: 'CNAMGS-123456',
    name: 'Jean Dupont',
    birthDate: '15/05/1985',
    phone: '+241 01 23 45 67',
    email: 'jean.dupont@email.ga',
    bloodType: 'A+',
    coverage: '80%',
    eligible: true,
    photo: 'https://randomuser.me/api/portraits/men/1.jpg',
    lastVisit: '2025-03-12',
    prescriptionsCount: 5,
    chronicDiseases: ['Hypertension'],
    riskLevel: 'Modéré',
  },
  {
    id: 'CNAMGS-789012',
    name: 'Marie Nzogho',
    birthDate: '23/09/1990',
    phone: '+241 01 23 45 68',
    email: 'marie.nzogho@email.ga',
    bloodType: 'O+',
    coverage: '75%',
    eligible: true,
    photo: 'https://randomuser.me/api/portraits/women/2.jpg',
    lastVisit: '2025-03-10',
    prescriptionsCount: 3,
    chronicDiseases: ['Asthme', 'Diabète type 2'],
    riskLevel: 'Élevé',
  },
  {
    id: 'CNAMGS-345678',
    name: 'Pierre Mba',
    birthDate: '10/12/1978',
    phone: '+241 01 23 45 69',
    email: 'pierre.mba@email.ga',
    bloodType: 'B+',
    coverage: '60%',
    eligible: false,
    photo: 'https://randomuser.me/api/portraits/men/3.jpg',
    lastVisit: '2025-03-05',
    prescriptionsCount: 2,
    chronicDiseases: [],
    riskLevel: 'Faible',
  },
  {
    id: 'CNAMGS-111222',
    name: 'Sophie Nguema',
    birthDate: '02/07/1995',
    phone: '+241 01 23 45 70',
    email: 'sophie.nguema@email.ga',
    bloodType: 'A-',
    coverage: '85%',
    eligible: true,
    photo: 'https://randomuser.me/api/portraits/women/4.jpg',
    lastVisit: '2025-03-15',
    prescriptionsCount: 4,
    chronicDiseases: [],
    riskLevel: 'Faible',
  },
];

const EligibilityCheck = () => {
  const [searchCode, setSearchCode] = useState('');
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = () => {
    if (!searchCode.trim()) {
      setError('Veuillez saisir un numéro d’assuré');
      setPatient(null);
      return;
    }

    setLoading(true);
    setError('');

    setTimeout(() => {
      const found = mockPatients.find(p => p.id === searchCode.trim());
      if (found) {
        setPatient(found);
        setError('');
      } else {
        setPatient(null);
        setError('Aucun patient trouvé avec ce numéro d’assuré');
      }
      setLoading(false);
    }, 500);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const getStatusBadge = (eligible) => {
    return eligible ? (
      <Badge bg="success" className="rounded-pill px-3 py-2">
        <CheckCircle size={12} className="me-1" /> Éligible
      </Badge>
    ) : (
      <Badge bg="danger" className="rounded-pill px-3 py-2">
        <XCircle size={12} className="me-1" /> Non éligible
      </Badge>
    );
  };

  const getRiskBadge = (level) => {
    const colors = { Faible: 'success', Modéré: 'warning', Élevé: 'danger' };
    return <Badge bg={colors[level]} className="rounded-pill px-3 py-2">{level}</Badge>;
  };

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4">
          <h4 className="main-title mb-0 d-flex align-items-center gap-2">
            <div className="bg-primary bg-opacity-10 rounded-circle p-2">
              <Shield size={24} className="text-primary" />
            </div>
            Vérification d'éligibilité
          </h4>
          <p className="text-muted mb-0 mt-1">
            Saisissez le numéro d’assuré pour vérifier les droits du patient
          </p>
        </div>

        {/* Carte de recherche */}
        <Card className="border-0 shadow-sm mb-4 bg-white" style={{ borderRadius: '20px' }}>
          <Card.Body className="p-4">
            <InputGroup>
              <InputGroup.Text className="bg-light border-0 rounded-start-pill">
                <QrCode size={18} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Numéro d’assuré (ex: CNAMGS-123456)"
                value={searchCode}
                onChange={(e) => setSearchCode(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="border-0 bg-light"
              />
              <Button
                variant="primary"
                onClick={handleSearch}
                disabled={loading}
                className="rounded-pill px-4"
              >
                {loading ? <Spinner size="sm" className="me-1" /> : <Search size={16} className="me-1" />}
                {loading ? 'Vérification...' : 'Vérifier'}
              </Button>
            </InputGroup>
          </Card.Body>
        </Card>

        {/* Gestion des erreurs */}
        {error && (
          <Alert variant="danger" className="border-0 rounded-3 shadow-sm">
            <InfoCircle size={18} className="me-2" />
            {error}
          </Alert>
        )}

        {/* Affichage du patient */}
        {patient && (
          <>
            {/* Carte principale avec photo et identité */}
            <Card className="border-0 shadow-lg mb-4" style={{ borderRadius: '28px', overflow: 'hidden' }}>
  <div className="position-relative">
    {/* Fond avec dégradé et motif léger */}
    <div style={{
      background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
      padding: '2rem',
      position: 'relative',
      zIndex: 1,
    }}>
      {/* Motif de points décoratif */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundImage: 'radial-gradient(circle at 25% 40%, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: '20px 20px',
        opacity: 0.4,
        pointerEvents: 'none',
        zIndex: 0,
      }} />
      <Row className="align-items-center position-relative" style={{ zIndex: 2 }}>
        <Col xs={12} md={3} className="text-center mb-4 mb-md-0">
          <div className="position-relative d-inline-block">
            <div style={{
              width: '140px',
              height: '140px',
              borderRadius: '50%',
              overflow: 'hidden',
              border: '4px solid rgba(255,255,255,0.8)',
              boxShadow: '0 15px 30px rgba(0,0,0,0.2)',
              background: '#f0f0f0',
            }}>
              <img
                src={patient.photo}
                alt={patient.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <div className="position-absolute bottom-0 end-0 bg-success rounded-circle p-1 border border-white shadow-sm">
              <CheckCircle size={14} className="text-white" />
            </div>
          </div>
          <div className="mt-3">
            {getStatusBadge(patient.eligible)}
          </div>
        </Col>
        <Col xs={12} md={9}>
          <h2 className="text-white mb-2 fw-bold" style={{ letterSpacing: '-0.5px' }}>{patient.name}</h2>
          <div className="d-flex flex-wrap gap-3 text-white-50 mb-3">
            <div className="d-flex align-items-center gap-1">
              <PersonCircle size={14} className="text-white-50" />
              <span>{patient.id}</span>
            </div>
            <div className="d-flex align-items-center gap-1">
              <Calendar size={14} className="text-white-50" />
              <span>Né(e) le {formatDate(patient.birthDate)} ({calculateAge(patient.birthDate)} ans)</span>
            </div>
          </div>
          <hr className="border-white-20 my-3" />
          <div className="d-flex flex-wrap gap-4">
            <div className="d-flex align-items-center gap-2 text-white">
              <Telephone size={16} />
              <span>{patient.phone}</span>
            </div>
            <div className="d-flex align-items-center gap-2 text-white">
              <Envelope size={16} />
              <span>{patient.email}</span>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  </div>
</Card>

            {/* Cartes de statistiques */}
            <Row className="g-3 mb-4">
              <Col md={6} lg={3} xs={6}>
                <Card className="border-0 shadow-sm stats-card h-100">
                  <div className="card-gradient" style={{ background: 'linear-gradient(135deg, #4361ee, #3b2a9f)' }}></div>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="text-muted mb-1 small">Taux de couverture</h6>
                        <h3 className="mb-0 fw-bold">{patient.coverage}</h3>
                        <ProgressBar now={parseInt(patient.coverage)} variant="primary" style={{ height: '5px', width: '100px' }} className="mt-2" />
                      </div>
                      <div className="icon-container bg-primary-subtle">
                        <Shield size={28} className="text-primary" />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} lg={3} xs={6}>
                <Card className="border-0 shadow-sm stats-card h-100">
                  <div className="card-gradient" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}></div>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="text-muted mb-1 small">Ordonnances</h6>
                        <h3 className="mb-0 fw-bold">{patient.prescriptionsCount}</h3>
                        <small className="text-muted">Ce trimestre</small>
                      </div>
                      <div className="icon-container bg-success-subtle">
                        <Prescription size={28} className="text-success" />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} lg={3} xs={6}>
                <Card className="border-0 shadow-sm stats-card h-100">
                  <div className="card-gradient" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}></div>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="text-muted mb-1 small">Dernière visite</h6>
                        <h3 className="mb-0 fw-bold fs-4">{formatDate(patient.lastVisit)}</h3>
                      </div>
                      <div className="icon-container bg-warning-subtle">
                        <CalendarEvent size={28} className="text-warning" />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6} lg={3} xs={6}>
                <Card className="border-0 shadow-sm stats-card h-100">
                  <div className="card-gradient" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}></div>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="text-muted mb-1 small">Risque</h6>
                        <div className="mt-1">{getRiskBadge(patient.riskLevel)}</div>
                      </div>
                      <div className="icon-container bg-danger-subtle">
                        <Heart size={28} className="text-danger" />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Informations détaillées (antécédents, etc.) */}
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-0 px-4">
                <h5 className="fw-semibold d-flex align-items-center gap-2">
                  <Activity size={18} className="text-primary" />
                  Informations médicales complémentaires
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                <Row className="g-4">
                  <Col md={6}>
                    <div className="bg-light rounded-3 p-3 h-100">
                      <h6 className="fw-semibold mb-3">Antécédents médicaux</h6>
                      {patient.chronicDiseases.length > 0 ? (
                        <div className="d-flex flex-wrap gap-2">
                          {patient.chronicDiseases.map((d, i) => (
                            <Badge key={i} bg="secondary" className="rounded-pill px-3 py-2">{d}</Badge>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted mb-0">Aucun antécédent notable</p>
                      )}
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="bg-light rounded-3 p-3 h-100">
                      <h6 className="fw-semibold mb-3">Informations biologiques</h6>
                      <div className="d-flex flex-wrap gap-3">
                        <div><Droplet size={14} className="text-danger me-1" /> Groupe sanguin : <strong>{patient.bloodType}</strong></div>
                        <div><Award size={14} className="text-warning me-1" /> Couverture : <strong>{patient.coverage}</strong></div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
              <Card.Footer className="bg-white border-0 pb-4 pt-0">
                <div className="d-flex justify-content-end gap-3">
                  <OverlayTrigger overlay={<Tooltip>Consulter l'historique complet</Tooltip>}>
                    <Button variant="outline-primary" className="rounded-pill px-4">
                      <FileText size={16} className="me-1" /> Dossier complet
                    </Button>
                  </OverlayTrigger>
                  <OverlayTrigger overlay={<Tooltip>Voir les statistiques de santé</Tooltip>}>
                    <Button variant="outline-info" className="rounded-pill px-4">
                      <GraphUp size={16} className="me-1" /> Activité
                    </Button>
                  </OverlayTrigger>
                </div>
              </Card.Footer>
            </Card>
          </>
        )}

        {/* Information générale */}
        <Row className="mt-4">
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)' }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Vérification en temps réel</strong>
                  <span className="small">
                    Les informations sont actualisées instantanément. En cas de non-éligibilité, veuillez contacter le service client.
                  </span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 4px;
            opacity: 0.8;
          }
          .icon-container {
            width: 48px;
            height: 48px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .border-white-20 {
            border-color: rgba(255, 255, 255, 0.2) !important;
          }
        `}</style>
      </div>
    </div>
  );
};

export default EligibilityCheck;